// Types for the generic forms platform. They mirror the Go JSON exactly
// (backend/internal/{forms,workflow,models}); keep them in sync.
import type { Condition } from '@/lib/expr'

export type FieldType =
    | 'text' | 'textarea' | 'number' | 'single_select' | 'multi_select' | 'yes_no'
    | 'date' | 'datetime' | 'file' | 'user_reference' | 'country' | 'html'

export interface FieldOption {
    value: string
    label: string
    legacyId?: string
}

export interface FormField {
    key: string
    stableId?: string
    type: FieldType
    label: string
    help?: string
    required?: boolean
    hidden?: boolean
    options?: FieldOption[]
    maxLength?: number
    visibleWhen?: Condition
}

export interface FormPage {
    id: string
    title: string
    description?: string
    group?: string
    activeWhen?: Condition
    fields: FormField[]
}

export type Severity = 'high' | 'medium' | 'low' | 'info'

export interface FormRule {
    id: string
    when: Condition
    field?: string
    severity: Severity
    section?: string
    moduleName?: string
    message: string
}

/** Per-form settings for the reviewer's AI risk summary. */
export interface AIReview {
    disabled?: boolean
    /** Plain-English review rules written by the form author. */
    instructions?: string
}

export const MAX_AI_INSTRUCTIONS = 4000

export interface FormDefinition {
    pages: FormPage[]
    rules: FormRule[]
    ai?: AIReview
}

export interface Form {
    id: string
    slug: string
    name: string
    category: string
    description: string
    number_prefix: string
    /** Front-page icon key; '' means automatic. */
    icon: string
    status: 'draft' | 'published' | 'archived'
    created_by: string
    created_at: string
    updated_at: string
    active_version: string
    draft_version: string
    submission_count: number
}

export interface FormVersion {
    form_id: string
    version: string
    status: 'draft' | 'published' | 'superseded'
    definition: FormDefinition
    checksum: string
    changelog: string
    published_at: string | null
    created_by: string
    created_at: string
}

export type Answers = Record<string, unknown>

export type SubmissionStatus = 'draft' | 'submitted' | 'in_review' | 'returned' | 'approved' | 'refused' | 'withdrawn'

export interface Submission {
    id: string
    number: string
    form_id: string
    form_slug: string
    form_name: string
    form_version: string
    status: SubmissionStatus
    answers: Answers
    title: string
    submitter_id: string
    submitter_name: string
    submitter_email: string
    current_page: number
    workflow_id: string | null
    current_step: number
    finding_count: number
    submitted_at: string | null
    decided_at: string | null
    created_at: string
    updated_at: string
}

export interface SubmissionFinding {
    id: string
    rule_id: string
    field_key: string
    section: string
    module_name: string
    message: string
    severity: Severity
    triggered_by_answer: string
}

export interface Person {
    id: string
    name: string
    email: string
    via: string
}

export type StepStatus = 'pending' | 'active' | 'awaiting_requester' | 'approved' | 'refused' | 'skipped'

export interface SubmissionStep {
    step: number
    name: string
    mode: 'any' | 'all'
    assignees: Person[]
    status: StepStatus
    round: number
}

export type DecisionKind = 'accept' | 'acknowledge' | 'approve_with_conditions' | 'refuse' | 'send_back'

export interface StepDecision {
    step: number
    round: number
    decision: DecisionKind
    comment: string
    decided_by: string
    decided_by_name: string
    decided_at: string
}

export interface SubmissionComment {
    id: string
    author_id: string
    author_name: string
    body: string
    internal: boolean
    created_at: string
}

export interface ConditionResponse {
    step: number
    round: number
    response: 'accepted' | 'declined'
    comment: string
    responded_by: string
    responded_by_name: string
    responded_at: string
}

export interface Revocation {
    step: number
    round: number
    reason: string
    revoked_by: string
    revoked_by_name: string
    revoked_at: string
}

export interface RoutedWorkflow {
    id: string
    name: string
    decisions: DecisionKind[]
    requireAttestation?: boolean
}

export type RiskLevel = 'low' | 'medium' | 'high'

export interface AIConcern {
    severity: RiskLevel
    title: string
    detail: string
    fields?: string[]
}

export interface AISummaryContent {
    overall_risk: RiskLevel
    headline: string
    summary: string
    concerns: AIConcern[]
    gaps: string[]
    questions_for_submitter: string[]
}

export interface AISummaryRecord {
    status: 'pending' | 'ready' | 'failed'
    model: string
    form_version: string
    summary: AISummaryContent | null
    error: string
    generated_at: string | null
    updated_at: string
}

/** Only present for reviewers and managers. */
export interface AIView {
    available: boolean
    summary: AISummaryRecord | null
}

export interface FileRecord {
    id: string
    submission_id: string
    field_key: string
    name: string
    content_type: string
    size_bytes: number
    sha256: string
    uploaded_by: string
    uploaded_at: string
}

/** normal: submitter or reviewer; override: manager needing a reason; none: no access. */
export type FileAccess = 'normal' | 'override' | 'none'

export type { UploadInfo } from '@/api/client'

export interface SubmissionDetail {
    files?: FileRecord[]
    file_access?: FileAccess
    /** True when the viewer reviews the current step and may correct the answers. */
    can_edit?: boolean
    ai?: AIView
    comments?: SubmissionComment[]
    condition_responses?: ConditionResponse[]
    revocations?: Revocation[]
    workflow: RoutedWorkflow | null
    submission: Submission
    definition: FormDefinition
    checksum: string
    findings: SubmissionFinding[]
    steps: SubmissionStep[]
    decisions: StepDecision[]
}

export interface AuditEvent {
    id: string
    actor_id: string
    actor_name: string
    action: string
    form_version: string
    changes: Record<string, unknown>
    answers_snapshot: Answers
    findings_snapshot: unknown
    created_at: string
}

// ---- workflows ----

export type AssigneeSpec =
    | { kind: 'user'; userId: string; name?: string }
    | { kind: 'group'; groupId: string }
    | { kind: 'rule'; rule: 'submitter_manager'; else?: AssigneeSpec[] }
    | { kind: 'rule'; rule: 'field'; field: string; else?: AssigneeSpec[] }
    | { kind: 'rule'; rule: 'conditional'; cases: { when: Condition; assignees: AssigneeSpec[] }[]; else: AssigneeSpec[] }

export interface WorkflowStep {
    order: number
    name: string
    mode: 'any' | 'all'
    assignees: AssigneeSpec[]
}

export interface WorkflowDefinition {
    steps: WorkflowStep[]
    decisions: DecisionKind[]
    requireAttestation?: boolean
    shareComment?: boolean
    autoEmail?: boolean
    allowSelfReview?: boolean
}

export interface Workflow {
    id: string
    name: string
    definition: WorkflowDefinition
    is_default: boolean
    archived: boolean
}

export interface RoutingRule {
    id?: string
    position?: number
    name: string
    condition: Condition
    workflow_id: string
    active: boolean
}

export interface Group {
    id: string
    name: string
    description: string
    member_count: number
    members?: { id: string; name: string; email: string }[]
}

export interface InboxItem extends Submission {
    step_name: string
}

export interface Problems {
    error: string
    problems?: string[]
    missing?: string[]
}
